import { useState } from 'react';
import FlowStepper from './FlowStepper';
import PlacementPreview from './PlacementPreview';
import SimulatedCheckout from './SimulatedCheckout';
import PipelineStages from './PipelineStages';
import {
  SPONSOR_PACKAGES,
  STATE_GROUPS,
  REGION_BY_ID,
  packageById,
  formatUSD,
} from './revenueData';
import type { Region } from './revenueData';

const STEPS = ['Package', 'Regions', 'Business', 'Creative', 'Summary', 'Checkout', 'Activation'];

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

type Business = { name: string; contact: string; email: string; website: string; tagline: string };

const EMPTY_BUSINESS: Business = { name: '', contact: '', email: '', website: '', tagline: '' };
const SAMPLE_BUSINESS: Business = {
  name: 'Harbor Point Grill',
  contact: 'Dana Whitfield',
  email: 'dana@example-demo.com',
  website: 'harborpoint.example.com',
  tagline: 'Weekend brunch on the water — 10 minutes from the course.',
};

export default function SponsorWorkflow() {
  const [step, setStep] = useState(0);
  const [packageId, setPackageId] = useState<string | null>(null);
  const [regionIds, setRegionIds] = useState<string[]>([]);
  const [business, setBusiness] = useState<Business>(EMPTY_BUSINESS);
  const [paid, setPaid] = useState(false);

  const pkg = packageId ? packageById(packageId) : undefined;
  const regionLabels = regionIds.map((id) => `${REGION_BY_ID[id].city}, ${REGION_BY_ID[id].state === 'California' ? 'CA' : 'AZ'}`);

  const toggleRegion = (id: string) => {
    setRegionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const canNext = (() => {
    if (step === 0) return Boolean(packageId);
    if (step === 1) return regionIds.length > 0;
    if (step === 2) return business.name.trim().length > 0 && business.email.trim().length > 0;
    return true;
  })();

  const go = (i: number) => setStep(Math.max(0, Math.min(STEPS.length - 1, i)));

  const restart = () => {
    setStep(0); setPackageId(null); setRegionIds([]); setBusiness(EMPTY_BUSINESS); setPaid(false);
  };

  const isCustom = pkg?.price === null;

  return (
    <div className="space-y-6">
      <FlowStepper steps={STEPS} current={step} label="Sponsorship workflow" onJump={go} />

      {/* STEP 0 — package */}
      {step === 0 && (
        <section>
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">Choose a sponsorship package</h3>
          <p className="mt-2 text-foreground-600 text-sm max-w-2xl">All prices are demonstration examples. The client picks a tier, then chooses the regions they want to reach.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {SPONSOR_PACKAGES.map((p) => {
              const active = packageId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPackageId(p.id)}
                  className={`text-left rounded-lg border p-5 cursor-pointer transition-colors ${active ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-400' : p.featured ? 'border-primary-300 bg-background-50 hover:border-primary-500' : 'border-background-200 bg-background-50 hover:border-primary-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-xl"><i className="ri-award-line"></i></span>
                    {p.featured && <span className="font-label uppercase tracking-widest text-[9px] font-bold bg-accent-100 text-accent-900 border border-accent-300 px-2 py-0.5 rounded-sm">Popular</span>}
                  </div>
                  <h4 className="mt-4 font-heading font-bold text-xl text-foreground-950 leading-tight">{p.name}</h4>
                  <p className="mt-1 font-label uppercase tracking-widest text-[10px] text-foreground-500">{p.tagline}</p>
                  <p className="mt-3 font-heading font-extrabold text-2xl text-foreground-950">{p.price === null ? 'Custom' : formatUSD(p.price)}<span className="text-xs font-body font-normal text-foreground-500"> {p.cadence}</span></p>
                  <ul className="mt-4 space-y-2">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-sm text-foreground-600">
                        <span className="w-4 h-4 flex items-center justify-center text-primary-500 shrink-0 mt-0.5"><i className="ri-check-line"></i></span>{perk}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* STEP 1 — regions */}
      {step === 1 && (
        <section>
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">Select target regions</h3>
          <p className="mt-2 text-foreground-600 text-sm max-w-2xl">Pick one or more markets for the campaign. Regions span two states, all served from this single platform.</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {STATE_GROUPS.map((group) => (
              <div key={group.state} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-label uppercase tracking-widest text-[11px] font-bold text-foreground-700">{group.state}</p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {group.regions.map((r: Region) => {
                    const on = regionIds.includes(r.id);
                    return (
                      <button key={r.id} type="button" onClick={() => toggleRegion(r.id)} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border font-label font-semibold text-xs whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                        <span className="w-4 h-4 flex items-center justify-center">{on ? <i className="ri-checkbox-circle-line"></i> : <i className="ri-add-circle-line"></i>}</span>
                        {r.city}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-foreground-500">{regionIds.length} region{regionIds.length === 1 ? '' : 's'} selected</p>
        </section>
      )}

      {/* STEP 2 — business info */}
      {step === 2 && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading font-bold text-2xl text-foreground-950">Sample business information</h3>
                <button type="button" onClick={() => setBusiness(SAMPLE_BUSINESS)} className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-magic-line"></i></span>
                  Use sample business
                </button>
              </div>
              <p className="mt-2 text-xs text-foreground-500 flex items-start gap-2">
                <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-lock-line"></i></span>
                Demonstration fields only. Nothing entered here is submitted, saved or sent anywhere.
              </p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label htmlFor="sw-name" className={labelClass}>Business name</label>
                  <input id="sw-name" value={business.name} onChange={(e) => setBusiness((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Harbor Point Grill" className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="sw-contact" className={labelClass}>Contact name</label>
                  <input id="sw-contact" value={business.contact} onChange={(e) => setBusiness((p) => ({ ...p, contact: e.target.value }))} placeholder="Full name" className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="sw-email" className={labelClass}>Contact email</label>
                  <input id="sw-email" type="email" value={business.email} onChange={(e) => setBusiness((p) => ({ ...p, email: e.target.value }))} placeholder="name@example-demo.com" className={fieldClass} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="sw-web" className={labelClass}>Website</label>
                  <input id="sw-web" value={business.website} onChange={(e) => setBusiness((p) => ({ ...p, website: e.target.value }))} placeholder="example.com" className={fieldClass} />
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <p className="font-label uppercase tracking-widest text-[11px] font-bold text-secondary-800">Why we ask</p>
              <p className="mt-2 text-sm text-secondary-900 leading-relaxed">In the live product this information would create the sponsor account and drive the campaign creative. Here it only populates the preview on the next step.</p>
            </div>
          </div>
        </section>
      )}

      {/* STEP 3 — creative */}
      {step === 3 && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading font-bold text-2xl text-foreground-950">Prepare the creative</h3>
              <p className="mt-2 text-sm text-foreground-600">In the full platform this step would be AI-assisted. Here you can type a tagline and watch the placement update.</p>
              <div className="mt-5">
                <label htmlFor="sw-tagline" className={labelClass}>Ad tagline</label>
                <textarea id="sw-tagline" rows={3} value={business.tagline} onChange={(e) => setBusiness((p) => ({ ...p, tagline: e.target.value }))} placeholder="A short line shown under your business name" className={`${fieldClass} resize-y`}></textarea>
              </div>
              <p className="mt-4 flex items-start gap-2 text-xs text-foreground-600 bg-accent-50 border border-accent-300 border-l-4 border-l-accent-500 rounded-md px-3 py-3">
                <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5 text-accent-700"><i className="ri-sparkling-line"></i></span>
                <span><strong className="font-semibold text-foreground-950">AI preparation is a simulation here.</strong> No model is connected; the creative you see is generated from the fields you typed.</span>
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <PlacementPreview brand={business.name} tagline={business.tagline} regions={regionLabels} packageName={pkg?.name || 'Package'} kind="sponsor" />
          </div>
        </section>
      )}

      {/* STEP 4 — summary */}
      {step === 4 && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading font-bold text-2xl text-foreground-950">Sample order summary</h3>
              <div className="mt-4 divide-y divide-background-200 border-y border-background-200">
                <div className="flex items-center justify-between py-3.5">
                  <span className="text-sm text-foreground-700">Package</span>
                  <span className="text-sm font-semibold text-foreground-950">{pkg?.name}</span>
                </div>
                <div className="flex items-start justify-between gap-4 py-3.5">
                  <span className="text-sm text-foreground-700">Regions</span>
                  <span className="text-sm text-foreground-950 text-right">{regionLabels.length ? regionLabels.join(', ') : '—'}</span>
                </div>
                <div className="flex items-center justify-between py-3.5">
                  <span className="text-sm text-foreground-700">Billing</span>
                  <span className="text-sm text-foreground-950">{pkg?.cadence}</span>
                </div>
                <div className="flex items-center justify-between py-4">
                  <span className="font-label uppercase tracking-widest text-xs text-foreground-600">Total</span>
                  <span className="font-heading font-extrabold text-3xl text-foreground-950">{pkg?.price === null ? 'Custom quote' : formatUSD(pkg?.price || 0)}</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-foreground-500">Illustrative pricing for demonstration. No subscription is created and no card is stored.</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="font-label uppercase tracking-widest text-[11px] font-bold text-foreground-700">What happens next</p>
              <ol className="mt-3 space-y-2.5 text-sm text-foreground-600">
                {['Checkout (simulated)', 'Editorial approval queue', 'Campaign scheduled & go live', 'Monthly performance report'].map((s, i) => (
                  <li key={s} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-foreground-950 text-accent-400 flex items-center justify-center font-label font-bold text-[11px] shrink-0">{i + 1}</span>{s}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* STEP 5 — checkout */}
      {step === 5 && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <SimulatedCheckout
              amountLabel={isCustom ? 'Custom quote' : `${formatUSD(pkg?.price || 0)} / mo`}
              summaryLine={`${pkg?.name} · ${regionIds.length} region${regionIds.length === 1 ? '' : 's'}`}
              done={paid}
              onSimulate={() => { setPaid(true); setStep(6); }}
            />
          </div>
          <div className="lg:col-span-5">
            <PipelineStages activeIndex={4} compact />
          </div>
        </section>
      )}

      {/* STEP 6 — activation */}
      {step === 6 && (
        <section className="space-y-6">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-2xl"><i className="ri-rocket-2-line"></i></span>
              <div>
                <h3 className="font-heading font-bold text-2xl text-foreground-950">Campaign activation &mdash; simulated</h3>
                <p className="text-sm text-foreground-600">{business.name || 'Your business'} · {pkg?.name}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-foreground-700">In the live product, the campaign would now be scheduled and pushed live automatically. In this demo it simply advances the pipeline &mdash; nothing is published, scheduled or charged.</p>
          </div>
          <PipelineStages activeIndex={8} />
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={restart} className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-restart-line"></i></span>
              Restart sponsorship demo
            </button>
          </div>
        </section>
      )}

      {/* Nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" onClick={() => go(step - 1)} disabled={step === 0} className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed text-foreground-700 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
          Back
        </button>
        {step < 5 && (
          <button type="button" onClick={() => go(step + 1)} disabled={!canNext} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-background-50 font-label font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            Continue
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
          </button>
        )}
      </div>
    </div>
  );
}